import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { coachingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    Video, 
    VideoOff, 
    Mic, 
    MicOff, 
    PhoneOff,
    Loader2,
    AlertTriangle,
    Clock,
    User,
    Phone,
    ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, differenceInMinutes } from 'date-fns';

export default function CoachingSession() {
    const navigate = useNavigate();
    const { bookingId } = useParams();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    
    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [inSession, setInSession] = useState(false);
    const [sessionReady, setSessionReady] = useState(false);
    
    const jitsiContainerRef = useRef(null);
    const [api, setApi] = useState(null);
    const [isAudioMuted, setIsAudioMuted] = useState(false);
    const [isVideoMuted, setIsVideoMuted] = useState(false);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        loadBooking();
    }, [user, bookingId]);

    const loadBooking = async () => {
        try {
            const res = await coachingAPI.getBookingDetails(bookingId);
            setBooking(res.data);
            
            // Check if session time is within 15 minutes
            const sessionTime = parseISO(`${res.data.booking_date}T${res.data.booking_time}:00`);
            const now = new Date();
            const minutesUntil = differenceInMinutes(sessionTime, now);
            
            if (minutesUntil <= 15 && minutesUntil >= -60) {
                setSessionReady(true);
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not load booking');
        } finally {
            setLoading(false);
        }
    };

    const startSession = () => {
        if (!sessionReady) {
            toast.error('Session is not ready yet. Please wait until 15 minutes before your scheduled time.');
            return;
        }
        setInSession(true);
        loadJitsi();
    };

    const loadJitsi = () => {
        // Load Jitsi Meet API script
        const script = document.createElement('script');
        script.src = 'https://meet.jit.si/external_api.js';
        script.async = true;
        script.onload = initJitsi;
        document.body.appendChild(script);
    };

    const initJitsi = () => {
        if (!window.JitsiMeetExternalAPI || !jitsiContainerRef.current) {
            console.error('Jitsi Meet API not loaded');
            return;
        }

        const domain = 'meet.jit.si';
        const roomName = `FA_Coaching_${bookingId}_Private`;
        
        const options = {
            roomName: roomName,
            parentNode: jitsiContainerRef.current,
            width: '100%',
            height: '100%',
            configOverwrite: {
                startWithAudioMuted: false,
                startWithVideoMuted: false,
                prejoinPageEnabled: false,
                disableDeepLinking: true,
                enableWelcomePage: false,
                enableClosePage: false,
                disableInviteFunctions: true,
                hideConferenceSubject: false,
                subject: `1-on-1 Coaching with Shelly`,
                // Security - private room
                requireDisplayName: true,
                enableLobbyChat: false,
            },
            interfaceConfigOverwrite: {
                TOOLBAR_BUTTONS: [
                    'microphone', 'camera', 'desktop', 'chat',
                    'raisehand', 'tileview', 'settings', 'fullscreen'
                ],
                SHOW_JITSI_WATERMARK: false,
                SHOW_WATERMARK_FOR_GUESTS: false,
                SHOW_BRAND_WATERMARK: false,
                BRAND_WATERMARK_LINK: '',
                SHOW_POWERED_BY: false,
                DEFAULT_BACKGROUND: '#1a1a2e',
                DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
                MOBILE_APP_PROMO: false,
                HIDE_INVITE_MORE_HEADER: true,
                DISABLE_FOCUS_INDICATOR: true,
            },
            userInfo: {
                displayName: user?.name || 'Student',
            }
        };

        const jitsiApi = new window.JitsiMeetExternalAPI(domain, options);
        
        jitsiApi.addListener('videoConferenceJoined', () => {
            toast.success('Connected to coaching session!');
        });

        jitsiApi.addListener('audioMuteStatusChanged', (data) => {
            setIsAudioMuted(data.muted);
        });

        jitsiApi.addListener('videoMuteStatusChanged', (data) => {
            setIsVideoMuted(data.muted);
        });

        jitsiApi.addListener('readyToClose', () => {
            endSession();
        });

        setApi(jitsiApi);
    };

    const toggleAudio = () => {
        if (api) {
            api.executeCommand('toggleAudio');
        }
    };

    const toggleVideo = () => {
        if (api) {
            api.executeCommand('toggleVideo');
        }
    };

    const endSession = () => {
        if (api) {
            api.dispose();
        }
        setInSession(false);
        toast.info('Session ended');
        navigate('/coaching');
    };

    const formatSessionTime = () => {
        if (!booking) return '';
        try {
            const hour = parseInt(booking.booking_time.split(':')[0]);
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const hour12 = hour > 12 ? hour - 12 : hour;
            return `${hour12}:00 ${ampm} MST`;
        } catch {
            return booking.booking_time;
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto px-4 py-12">
                <Card className="max-w-md mx-auto p-8 text-center">
                    <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
                    <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
                    <p className="text-muted-foreground mb-6">{error}</p>
                    <Button onClick={() => navigate('/coaching')} className="rounded-full">
                        Back to Coaching
                    </Button>
                </Card>
            </div>
        );
    }

    // In-session view
    if (inSession) {
        return (
            <div className="h-screen flex flex-col bg-[#1a1a2e]">
                {/* Header */}
                <div className="bg-[#16213e] px-4 py-3 flex items-center justify-between border-b border-gray-700">
                    <div className="flex items-center gap-3">
                        <Badge className="bg-green-500 flex items-center gap-1">
                            <Video className="h-3 w-3" />
                            1-on-1 Session
                        </Badge>
                        <span className="text-white font-medium">Coaching with Shelly</span>
                    </div>
                    <div className="text-gray-300 text-sm">
                        {booking?.booking_date} at {formatSessionTime()}
                    </div>
                </div>

                {/* Video Container */}
                <div className="flex-1 relative">
                    <div 
                        ref={jitsiContainerRef} 
                        className="w-full h-full"
                        style={{ minHeight: '500px' }}
                    />
                </div>

                {/* Custom Controls */}
                <div className="bg-[#16213e] px-4 py-4 flex items-center justify-center gap-4 border-t border-gray-700">
                    <Button
                        variant={isAudioMuted ? "destructive" : "secondary"}
                        size="lg"
                        className="rounded-full h-14 w-14"
                        onClick={toggleAudio}
                        data-testid="coaching-toggle-audio"
                    >
                        {isAudioMuted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
                    </Button>
                    
                    <Button
                        variant={isVideoMuted ? "destructive" : "secondary"}
                        size="lg"
                        className="rounded-full h-14 w-14"
                        onClick={toggleVideo}
                        data-testid="coaching-toggle-video"
                    >
                        {isVideoMuted ? <VideoOff className="h-6 w-6" /> : <Video className="h-6 w-6" />}
                    </Button>
                    
                    <Button
                        variant="destructive"
                        size="lg"
                        className="rounded-full h-14 w-14 bg-red-600 hover:bg-red-700"
                        onClick={endSession}
                        data-testid="coaching-end-session"
                    >
                        <PhoneOff className="h-6 w-6" />
                    </Button>
                </div>
            </div>
        );
    }

    // Pre-session lobby
    return (
        <div className="container mx-auto px-4 py-12">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-2xl mx-auto"
            >
                <Button 
                    variant="ghost" 
                    className="mb-6"
                    onClick={() => navigate('/coaching')}
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Coaching
                </Button>

                <Card className="p-8">
                    <div className="text-center mb-8">
                        <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                            <Video className="h-10 w-10 text-primary" />
                        </div>
                        <h1 className="font-heading text-2xl font-bold mb-2">
                            Your Coaching Session
                        </h1>
                        <p className="text-muted-foreground">
                            Private 1-on-1 video session with Shelly
                        </p>
                    </div>

                    {/* Session Details */}
                    <div className="bg-muted/50 rounded-lg p-6 mb-6">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center gap-3">
                                <Clock className="h-5 w-5 text-muted-foreground" />
                                <div>
                                    <p className="text-sm text-muted-foreground">Date & Time</p>
                                    <p className="font-medium">
                                        {booking?.booking_date}<br />
                                        {formatSessionTime()}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                {booking?.session_type === 'coaching_zoom' ? (
                                    <Video className="h-5 w-5 text-muted-foreground" />
                                ) : (
                                    <Phone className="h-5 w-5 text-muted-foreground" />
                                )}
                                <div>
                                    <p className="text-sm text-muted-foreground">Session Type</p>
                                    <p className="font-medium">
                                        {booking?.session_type === 'coaching_zoom' ? 'Video Call' : 'Phone Call'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Status */}
                    {sessionReady ? (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                            <div className="flex items-center gap-2 text-green-700">
                                <Video className="h-5 w-5" />
                                <span className="font-medium">Session is ready to start!</span>
                            </div>
                            <p className="text-sm text-green-600 mt-1">
                                Click the button below to join the video room.
                            </p>
                        </div>
                    ) : (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                            <div className="flex items-center gap-2 text-yellow-700">
                                <Clock className="h-5 w-5" />
                                <span className="font-medium">Session not ready yet</span>
                            </div>
                            <p className="text-sm text-yellow-600 mt-1">
                                The video room will be available 15 minutes before your scheduled time.
                            </p>
                        </div>
                    )}

                    {/* Join Button */}
                    {booking?.session_type === 'coaching_zoom' ? (
                        <Button 
                            className="w-full rounded-full h-12 text-lg gap-2"
                            onClick={startSession}
                            disabled={!sessionReady}
                            data-testid="join-coaching-session"
                        >
                            <Video className="h-5 w-5" />
                            {sessionReady ? 'Join Video Session' : 'Session Not Ready'}
                        </Button>
                    ) : (
                        <div className="text-center p-4 bg-muted rounded-lg">
                            <Phone className="h-8 w-8 text-primary mx-auto mb-2" />
                            <p className="font-medium">Phone Session</p>
                            <p className="text-sm text-muted-foreground">
                                Shelly will call you at the scheduled time.<br />
                                Make sure your phone doesn't block unknown callers.
                            </p>
                        </div>
                    )}

                    {/* Reminders */}
                    <div className="mt-6 pt-6 border-t">
                        <h3 className="font-medium mb-3">Before your session:</h3>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                            <li className="flex items-start gap-2">
                                <span className="text-primary">•</span>
                                Find a quiet, well-lit space
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-primary">•</span>
                                Test your camera and microphone
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-primary">•</span>
                                Have your questions ready
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-primary">•</span>
                                Be ready 5 minutes early
                            </li>
                        </ul>
                    </div>
                </Card>
            </motion.div>
        </div>
    );
}
